import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { List } from 'semantic-ui-react'
import { useApolloClient } from '@apollo/client'
import { toast } from '@/ui'
import { Trans } from 'react-i18next'

import { Alert, Modal, Button, Form, FlexRow } from '@/ui'
import { useMultiSelect } from '@/hooks'
import { logError } from '@/helpers/log-error'
import getMutationErrorMessage from '@/helpers/get-mutation-error-message'
import SuperWarning from './SuperWarning'
import AlertWithMutation from './AlertWithMutation'

const StyledList = styled(List).attrs({
  selection: true,
  divided: true,
  verticalAlign: 'middle',
})`
  max-height: 50vh;
  overflow: auto;
`

const StyledCheckbox = styled(Form.Checkbox)`
  line-height: 0;
  margin-right: 0.5em !important;
`

const ConfirmComponent = ({
  text,
  query,
  handleSubmit,
  afterSubmit,
  acceptButtonText,
  acceptedText,
  title,
  t,
  onClose,
  onSubmit,
  items: initialItems = [],
  renderItem,
  mutation,
  successText,
  disabledItem,
  description,
  alertType,
  superWarning,
  submitErrorMessage,
  acceptIcon,
}) => {
  const [selected, toggleSelected, setSelected] = useMultiSelect([])

  const [items, setItems] = useState(initialItems)

  const [loading, setLoading] = useState(false)

  const [submitting, setSubmitting] = useState(false)

  const client = useApolloClient()

  useEffect(() => {
    if (query) {
      const fetchItems = async () => {
        try {
          setLoading(true)

          const { data } = await client.query(query)

          setItems(data[Object.keys(data)[0]].edges)
        } catch (e) {
          logError(e)
          toast.error(getMutationErrorMessage(e, t))
        } finally {
          setLoading(false)
        }
      }

      fetchItems()
    }
  }, [client, query, t])

  useEffect(() => {
    setSelected(items.map(({ node }) => node.id))
  }, [items, setSelected])

  const handleFullSubmit = useCallback(async () => {
    setSubmitting(true)

    try {
      const filteredItems = items.filter(
        ({ node }) =>
          selected.includes(node.id) && !(disabledItem && !disabledItem(node)),
      )

      let result

      if (handleSubmit) {
        setSubmitting(true)

        result = await handleSubmit(filteredItems)

        if (afterSubmit) {
          await afterSubmit(result)
        }
      } else {
        result = filteredItems
      }

      return onSubmit(result)
    } catch (e) {
      setSubmitting(false)

      if (submitErrorMessage) {
        submitErrorMessage(e, items)
      } else {
        toast.error(getMutationErrorMessage(e, t))
      }
    }
  }, [
    afterSubmit,
    handleSubmit,
    items,
    onSubmit,
    selected,
    disabledItem,
    submitErrorMessage,
    t,
  ])

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Enter') {
        handleFullSubmit()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleFullSubmit])

  if (superWarning) {
    return (
      <SuperWarning
        title={title}
        successText={successText}
        t={t}
        description={description}
        mutation={mutation}
        onCancel={() => onClose()}
        onSubmit={handleFullSubmit}
        acceptButtonText={acceptButtonText}
        acceptedText={acceptedText}
      />
    )
  }
  if (mutation) {
    return (
      <AlertWithMutation
        title={title}
        successText={successText}
        t={t}
        description={description}
        mutation={mutation}
        onCancel={() => onClose()}
        onSubmit={handleFullSubmit}
        acceptButtonText={acceptButtonText}
        acceptedText={acceptedText}
        acceptIcon={acceptIcon}
      />
    )
  }

  if (alertType) {
    return (
      <Alert
        type={alertType}
        title={title}
        message={description}
        cancelText={t('No')}
        submitText={t('Yes')}
        onCancel={() => onClose()}
        onSubmit={handleFullSubmit}
      />
    )
  }

  return (
    <Modal
      size="tiny"
      onClose={() => onClose()}
      loading={loading || submitting}>
      {title && <Modal.Header>{title}</Modal.Header>}

      <Modal.Content image>
        <Modal.Description style={{ width: '100%' }}>
          {!!items.length && renderItem ? (
            <>
              <Trans>
                You have selected{' '}
                <strong>{{ selectedLength: selected.length }}</strong>{' '}
                {{ itemName: text || t('items') }}
              </Trans>
              <StyledList>
                {items.map(({ node }) => {
                  const disabled = disabledItem && !disabledItem(node)

                  return (
                    <List.Item
                      key={node.id}
                      onClick={
                        !disabled ? () => toggleSelected(node.id) : undefined
                      }>
                      <FlexRow
                        noMargin
                        justifyContent="flex-start"
                        alignItems="center"
                        style={{ color: '#000' }}>
                        <StyledCheckbox
                          disabled={disabled}
                          checked={selected.includes(node.id)}
                        />
                        {renderItem(node)}
                      </FlexRow>
                    </List.Item>
                  )
                })}
              </StyledList>
            </>
          ) : (
            description
          )}
        </Modal.Description>
      </Modal.Content>

      <Modal.Actions>
        <FlexRow noMargin style={{ padding: '0 .5em' }}>
          <Button.Cancel onClick={() => onClose()} content={t('Cancel')} />
          <Button
            onClick={handleFullSubmit}
            content={'Ok'}
            disabled={!!(items.length && !selected.length)}
          />
        </FlexRow>
      </Modal.Actions>
    </Modal>
  )
}

ConfirmComponent.propTypes = {
  title: PropTypes.string,
  text: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  query: PropTypes.shape({
    query: PropTypes.object.isRequired,
    variables: PropTypes.object.isRequired,
    fetchPolicy: PropTypes.string,
  }),
  variant: PropTypes.oneOf(['save', 'delete', 'password']),
  items: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  renderItem: PropTypes.func,
  handleSubmit: PropTypes.func,
  afterSubmit: PropTypes.func,
  t: PropTypes.func,
}

export default ConfirmComponent
